import { createApi, type BaseFetchQueryFnArgs } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { transformResponseWithCurrentUserPushMessages } from 'BackRoyalApi/transformResponseWithCurrentUserPushMessages';
import { type BaseQueryApi, type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { angularInjectorProvider } from 'Injector';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import { type FrontRoyalRootScope } from 'FrontRoyalAngular';
import { getProgressSuspendableResource } from 'ContentProgress';
import { UPLOAD_PROJECT_SUBMISSION_ROUTE } from './constants';
import { type ProjectSubmissionUploadAttrs, type UploadProjectSubmissionResponse } from './ProjectSubmission.types';

// Type for the baseQuery parameter passed to queryFn (already has api and extraOptions applied)
// MaybePromise allows both sync and async returns, matching RTK Query's internal type
type QueryFnBaseQueryResult = { data?: unknown; error?: FetchBaseQueryError; meta?: Record<string, unknown> };
type QueryFnBaseQuery = (args: BaseFetchQueryFnArgs) => QueryFnBaseQueryResult | PromiseLike<QueryFnBaseQueryResult>;

const attachmentsHaveFiles = (upload: ProjectSubmissionUploadAttrs): boolean =>
    upload.attachments.some(attachment => attachment.file);

// Appends a file part, reading the bytes rather than passing the picked File straight through.
// Chromium records a File's size and mtime when the picker creates it and re-checks them when the
// request body is read; a mismatch aborts the upload with ERR_UPLOAD_FILE_CHANGED, which cloud
// providers (Google Drive, iCloud, Dropbox) trigger reliably because they hydrate placeholder files on
// first read. The Blob that replaces the File carries no name, so the filename goes in explicitly.
const appendFile = async (formData: FormData, key: string, file: File) => {
    const blob = new Blob([await file.arrayBuffer()], { type: file.type });
    formData.append(key, blob, file.name);
};

// Rails expects indexed arrays like: attachments[0][file], attachments[0][submission_format_type], etc.
const buildFormDataWithAttachments = async (upload: ProjectSubmissionUploadAttrs): Promise<FormData> => {
    const formData = new FormData();

    formData.append('record[user_id]', upload.user_id);
    formData.append('record[project_type]', upload.project_type);
    formData.append('record[requirement_identifier]', upload.requirement_identifier);
    formData.append('record[submitted_for_learner_project_id]', upload.submitted_for_learner_project_id);
    formData.append('record[metadata][cohort_id]', upload.metadata.cohort_id);
    if (upload.metadata.from_project_eval_modal) {
        formData.append('record[metadata][from_project_eval_modal]', 'true');
    }

    if (upload.group_project_agreement_file) {
        await appendFile(formData, 'record[group_project_agreement_file]', upload.group_project_agreement_file);
    }

    if (upload.selected_project_variant) {
        formData.append('record[selected_project_variant]', upload.selected_project_variant);
    }

    for (let index = 0; index < upload.attachments.length; index++) {
        const attachment = upload.attachments[index];

        formData.append(`record[attachments][${index}][submission_format_type]`, attachment.submission_format_type);

        if (attachment.file) {
            await appendFile(formData, `record[attachments][${index}][file]`, attachment.file);
        } else if (attachment.url) {
            formData.append(`record[attachments][${index}][url]`, attachment.url);
        }
    }

    return formData;
};

const notifyFileUnreadable = (upload: ProjectSubmissionUploadAttrs, e: unknown) => {
    const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
    ErrorLogService?.notify('Could not read a project submission file', e, {
        attachment_file_count: upload.attachments.filter(attachment => attachment.file).length,
        has_group_project_agreement_file: !!upload.group_project_agreement_file,
    });
};

const buildUploadBody = async (
    upload: ProjectSubmissionUploadAttrs,
): Promise<{ body: { record: ProjectSubmissionUploadAttrs } | FormData } | { error: FetchBaseQueryError }> => {
    if (!attachmentsHaveFiles(upload) && !upload.group_project_agreement_file) {
        return { body: { record: upload } };
    }

    try {
        return { body: await buildFormDataWithAttachments(upload) };
    } catch (e) {
        notifyFileUnreadable(upload, e);
        return { error: { status: 'CUSTOM_ERROR', error: e instanceof Error ? e.message : String(e) } };
    }
};

// Exported for testing
export { buildFormDataWithAttachments, attachmentsHaveFiles, buildUploadBody };

export const uploadProjectSubmissionApi = createApi('UploadProjectSubmission', {
    ...getBackRoyalApiBaseConfig('UploadProjectSubmission'),
    endpoints: builder => ({
        create: builder.mutation<UploadProjectSubmissionResponse, ProjectSubmissionUploadAttrs>({
            async queryFn(
                projectSubmissionUpload: ProjectSubmissionUploadAttrs,
                _api: BaseQueryApi,
                _options: Record<string, unknown>,
                fetchWithBaseQuery: QueryFnBaseQuery,
            ) {
                const bodyResult = await buildUploadBody(projectSubmissionUpload);
                if ('error' in bodyResult) return { error: bodyResult.error };

                const response = (await fetchWithBaseQuery({
                    url: UPLOAD_PROJECT_SUBMISSION_ROUTE,
                    method: 'POST',
                    body: bodyResult.body,
                })) as {
                    data?: UploadProjectSubmissionResponse;
                    error?: FetchBaseQueryError;
                };

                if (response.error) {
                    return { error: response.error };
                }

                // Api::ProjectSubmissionsController#upload will create a ProjectProgress for the ProjectSubmission.
                // We want to do a few things here (and block the completion of this mutation until they're done):
                //  1. Force UserProgressLoader to refetch progress using the max updated_at currently in store, which at this point will not include in the ProjectProgress we just created.
                //     Ordinarily, this would be handled by UserProgressLoaderInterceptor, but this is the first API call we've written that creates progress outside of Angular.js, meaning UserProgressLoaderInterceptor won't run on this response.
                //  2. Force a reload of UserProgressLoader.suspendableResource because the value will be cached and won't be reloaded until UserProgressLoader.suspendableResource pings on its own interval.
                //
                // This is hopefully a temporary solution, ideally we would be querying for progress via RTKQ and simply invalidating tags here.
                const { pushMessages } = response.data?.meta as UploadProjectSubmissionResponse['meta'];
                const currentUser = angularInjectorProvider.get<FrontRoyalRootScope>('$rootScope').currentUser;
                const userProgressLoader = currentUser?.progress;
                if (userProgressLoader) {
                    const progressMaxUpdatedAt = await userProgressLoader.getProgressMaxUpdatedAt();
                    await userProgressLoader.onUnfetchedUpdatesDetected(progressMaxUpdatedAt);
                    await userProgressLoader.suspendableResource.load().promise;
                    // Also force-reload the React-side module-level resource used by useContentProgress.
                    // Without this, components like ProjectSubmissionForm won't see the new ProjectProgress
                    // until the 60-second ping interval fires.
                    if (currentUser.id) {
                        await getProgressSuspendableResource(currentUser.id).load().promise;
                    }
                }

                // Push any updated properties onto the CurrentUserIguanaObject.
                // This is important because this mutation can specifically change the past_due_projects on a user's program inclusion.
                transformResponseWithCurrentUserPushMessages(pushMessages);

                return { data: response.data as UploadProjectSubmissionResponse };
            },
        }),
    }),
});
